const DEFAULT_PROFILE_IMAGE = 'https://imagedelivery.net/hAeIC__6Aj746x0RFU1joA/b640dc78-9146-4a0e-5d34-1d25ced2ce00/personalEmailSignature';
const SITE_URL = 'https://suarte.art';

function normalizeString(value) {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

function truncateText(value, maxLength = 160) {
  const normalized = normalizeString(value);
  if (!normalized || normalized.length <= maxLength) {
    return normalized;
  }

  const shortened = normalized.slice(0, Math.max(0, maxLength - 1)).trim();
  const lastSpaceIndex = shortened.lastIndexOf(' ');
  const safeValue = lastSpaceIndex > 80 ? shortened.slice(0, lastSpaceIndex) : shortened;
  return `${safeValue.trim()}...`;
}

function appendImageVariant(imageUrl, variant = 'w=1200') {
  const normalized = normalizeString(imageUrl);
  if (!normalized) {
    return DEFAULT_PROFILE_IMAGE;
  }

  const alreadyVariant = /\/[a-z0-9=_-]+$/i.test(normalized);
  if (alreadyVariant) {
    return normalized;
  }

  return normalized.endsWith('/') ? `${normalized}${variant}` : `${normalized}/${variant}`;
}

function resolveProfileType(data = {}) {
  return normalizeString(data?.user_type || data?.__t).toLowerCase();
}

function resolveProfileName(data = {}) {
  const profileType = resolveProfileType(data);
  if (profileType === 'collector') {
    return normalizeString(data?.user_username) || 'Collector';
  }

  return normalizeString(data?.user_name) || normalizeString(data?.user_username) || 'Suarte Profile';
}

function resolveProfileLocation(data = {}) {
  const city = normalizeString(data?.gallery_address?.city);
  const country = normalizeString(
    data?.gallery_address?.country
    || data?.gallery_address?.address_country
  );
  const profileLocation = normalizeString(data?.user_profile_info?.user_location);

  if (city && country) {
    return {
      city,
      country,
      label: `${city}, ${country}`,
    };
  }

  if (country) {
    return {
      city: '',
      country,
      label: country,
    };
  }

  if (profileLocation) {
    return {
      city: '',
      country: '',
      label: profileLocation,
    };
  }

  return {
    city: '',
    country: '',
    label: '',
  };
}

function resolveProfileImage(data = {}) {
  return appendImageVariant(data?.user_image?.image_url, 'w=1200');
}

function isGalleryIndexable(data = {}) {
  return resolveProfileType(data) === 'gallery'
    && Boolean(data?.user_flags?.onboarding_completed)
    && !data?.gallery_closed
    && normalizeString(data?.user_username).toLowerCase() !== 'suarte';
}

function isArtistIndexable(data = {}) {
  return resolveProfileType(data) === 'artist'
    && Boolean(data?.user_flags?.onboarding_completed)
    && Number(data?.user_profile_info?.user_artworks) > 0
    && normalizeString(data?.user_username).toLowerCase() !== 'suarte';
}

function isCollectorIndexable(data = {}) {
  return resolveProfileType(data) === 'collector'
    && Boolean(data?.user_flags?.onboarding_completed)
    && Number(data?.user_profile_info?.user_artworks) > 0
    && normalizeString(data?.user_username).toLowerCase() !== 'suarte';
}

function isPublicProfileIndexable(data = {}) {
  const profileType = resolveProfileType(data);

  if (profileType === 'gallery') {
    return isGalleryIndexable(data);
  }

  if (profileType === 'artist') {
    return isArtistIndexable(data);
  }

  if (profileType === 'collector') {
    return isCollectorIndexable(data);
  }

  return false;
}

function buildGalleryDescription(data = {}) {
  const explicitDescription = truncateText(data?.user_profile_info?.user_description, 160);
  if (explicitDescription) {
    return explicitDescription;
  }

  const galleryName = resolveProfileName(data);
  const location = resolveProfileLocation(data).label;
  const artworkCount = Number(data?.user_profile_info?.user_artworks) || 0;
  const locationFragment = location ? ` based in ${location}` : '';
  const artworkFragment = artworkCount > 0
    ? ` Explore ${artworkCount} available artwork${artworkCount === 1 ? '' : 's'}`
    : ' Explore available artworks';

  return truncateText(
    `${galleryName}${locationFragment} on Suarte.${artworkFragment} and discover original contemporary art from represented artists.`,
    160
  );
}

function buildArtistDescription(data = {}) {
  const explicitDescription = truncateText(data?.user_profile_info?.user_description, 160);
  if (explicitDescription) {
    return explicitDescription;
  }

  const artistName = resolveProfileName(data);
  const artworkCount = Number(data?.user_profile_info?.user_artworks) || 0;
  return truncateText(
    `Discover ${artistName}'s profile on Suarte${artworkCount > 0 ? ` and explore ${artworkCount} artwork${artworkCount === 1 ? '' : 's'}` : ''}.`,
    160
  );
}

function buildCollectorDescription(data = {}) {
  const explicitDescription = truncateText(data?.user_profile_info?.user_description, 160);
  if (explicitDescription) {
    return explicitDescription;
  }

  const collectorName = resolveProfileName(data);
  const collectionCount = Number(data?.user_profile_info?.user_artworks) || 0;
  return truncateText(
    `${collectorName} is building a public collection on Suarte${collectionCount > 0 ? ` with ${collectionCount} collected artwork${collectionCount === 1 ? '' : 's'}` : ''}. Explore their art journey and collecting perspective.`,
    160,
  );
}

function buildProfileDescription(data = {}) {
  const profileType = resolveProfileType(data);
  if (profileType === 'gallery') {
    return buildGalleryDescription(data);
  }
  if (profileType === 'artist') {
    return buildArtistDescription(data);
  }
  return buildCollectorDescription(data);
}

function buildProfileSchema(data = {}, metadata = {}) {
  const profileType = resolveProfileType(data);
  const name = resolveProfileName(data);
  const location = resolveProfileLocation(data);
  const profileEntityType = profileType === 'gallery' ? 'Organization' : 'Person';
  const followerCount = Number(data?.user_profile_info?.user_inspiring) || 0;
  const likeCount = Number(data?.user_profile_info?.user_likes) || 0;
  const artworkCount = Number(data?.user_profile_info?.user_artworks) || 0;

  const mainEntity = {
    '@type': profileEntityType,
    '@id': `${metadata.canonicalUrl}#entity`,
    name,
    alternateName: normalizeString(data?.user_username) || undefined,
    description: metadata.description,
    image: metadata.image,
    url: metadata.canonicalUrl,
  };

  if (profileType === 'gallery' && location.label) {
    mainEntity.address = {
      '@type': 'PostalAddress',
      addressLocality: location.city || undefined,
      addressCountry: location.country || location.label,
    };
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'ProfilePage',
        name: metadata.title,
        description: metadata.description,
        url: metadata.canonicalUrl,
        primaryImageOfPage: metadata.image,
        mainEntity: {
          '@id': `${metadata.canonicalUrl}#entity`,
        },
      },
      {
        ...mainEntity,
        interactionStatistic: [
          {
            '@type': 'InteractionCounter',
            interactionType: 'https://schema.org/FollowAction',
            userInteractionCount: followerCount,
          },
          {
            '@type': 'InteractionCounter',
            interactionType: 'https://schema.org/LikeAction',
            userInteractionCount: likeCount,
          },
          {
            '@type': 'InteractionCounter',
            interactionType: 'https://schema.org/WriteAction',
            userInteractionCount: artworkCount,
          },
        ],
      },
    ],
  };
}

export function buildProfileSeoMetadata(data = {}, options = {}) {
  if (data?.__seo) {
    return {
      title: data.__seo.title,
      description: data.__seo.metaDescription || data.__seo.description || '',
      image: data.__seo.ogImage || DEFAULT_PROFILE_IMAGE,
      canonicalUrl: data.__seo.canonicalUrl || SITE_URL,
      robots: data.__seo.robots || 'noindex,follow',
      profileType: data.__seo.type || resolveProfileType(data),
      schema: data.__seo.structuredData || {},
    };
  }

  const { internal = false, pathname = '' } = options;
  const username = normalizeString(data?.user_username);
  const basePath = username ? `/user/${username}` : '';
  const isPublicBaseRoute = !internal && Boolean(basePath) && pathname === basePath;
  const canonicalUrl = username ? `${SITE_URL}${basePath}` : SITE_URL;
  const title = `${resolveProfileName(data)} | Suarte`;
  const description = buildProfileDescription(data);
  const image = resolveProfileImage(data);
  const profileType = resolveProfileType(data);
  const canIndex = isPublicBaseRoute && isPublicProfileIndexable(data);
  const robots = canIndex ? 'index,follow,max-image-preview:large' : 'noindex,follow';

  const metadata = {
    title,
    description,
    image,
    canonicalUrl,
    robots,
    profileType,
  };

  return {
    ...metadata,
    schema: buildProfileSchema(data, metadata),
  };
}
